/**
 * geoLevel.ts — the geographic level a metric is actually measured at (T-537).
 *
 * Noreen batch-3 item 1: every metric shown to a user should say which geography it describes.
 * Before this card the app implied ONE answer for everything — `scoreDefinitions.ts` carried a
 * free-text `geoScope` hardcoded to "Census-tract level" on all 16 entries (and rendered nowhere),
 * while the 33 metrics in the All-metrics card said nothing at all. That was wrong for the two
 * county-sourced metrics and unverifiable for the rest.
 *
 * WHAT A LEVEL MEANS HERE: the finest geography at which THIS tract's number is distinct — i.e.
 * does the value vary tract to tract, or is it inherited unchanged from a coarser unit? That is
 * the question a user comparing two tracts actually has. `evRegPer1000` and `popGrowth5yr` are
 * identical for every tract in the same county, so they are county-level no matter that they are
 * stored on a tract row. A metric aggregated UP from finer features (LODES blocks → tract, charging
 * ports → tract) still resolves to a per-tract number, so it is tract-level; the finer source
 * granularity is not misleading and is recorded in the per-metric provenance comments instead.
 *
 * This module is DATA + vocabulary only — no presentation. The compact badge lives in
 * `components/GeoLevelTag.tsx`, so switching from per-metric tags to grouping the card by level
 * (Noreen's open question) is a change in one component, not 33 edits.
 */

/**
 * The census geographies the app can honestly claim, plus `site` for the point-level score.
 *
 * `block` and `block-group` are in the union because the app already computes and shows block /
 * block-group classifications (see `locationClassification.ts`, T-498/T-512) — a metric at those
 * levels is a real near-term possibility, and the vocabulary should not have to change to admit
 * one. No metric currently uses them.
 */
export type GeoLevel = 'block' | 'block-group' | 'tract' | 'county' | 'site'

/** Every level, for exhaustiveness checks and tests. Ordered finest → coarsest, then site. */
export const GEO_LEVELS = ['block', 'block-group', 'tract', 'county', 'site'] as const

/**
 * Compact tag text. Deliberately matches the wording `locationClassification.ts` already puts in
 * front of users ("block", "block group") rather than introducing census jargon like "Block Group
 * (BG)" — one vocabulary for one geography.
 */
export const GEO_LEVEL_LABELS: Record<GeoLevel, string> = {
  block: 'Block',
  'block-group': 'Block group',
  tract: 'Tract',
  county: 'County',
  site: 'Site',
}

/**
 * One-line explanation for a tooltip/aria-label. The county line is the one that carries real
 * information: it tells the user why two tracts in the same county show the same number.
 */
export const GEO_LEVEL_DESCRIPTIONS: Record<GeoLevel, string> = {
  block: 'Measured for this census block.',
  'block-group': 'Measured for this census block group.',
  tract: 'Measured for this census tract.',
  county: 'Measured for the whole county — every tract in this county shows the same value.',
  site: 'Measured for this specific site, not the surrounding area.',
}
